import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useForm } from "react-hook-form";
import Modal from "/src/components/modal/Modal";
import useModalHelper from "/src/components/modal/hooks/useModalHelper";
import Form from "/src/components/form/Form";
import Field from "/src/components/UI/Field/Field";
import TextInput from "/src/components/UI/TextField/TextInput";
import Button from "/src/components/UI/Button/Button";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";
import accountService from "../../services/accountService";
import "./ChangePasswordModal.css";

function parseErrorDetail(error, fallback) {
    try {
        return JSON.parse(error?.body)?.detail || fallback;
    } catch {
        return fallback;
    }
}

const ChangePasswordModal = NiceModal.create(() => {
    const modal = useModal();
    const { onClose } = useModalHelper(modal);
    const toast = useToastDispatchContext();

    const { register, handleSubmit, watch, formState: { errors, isValid } } = useForm({
        mode: "onChange",
        defaultValues: { current_password: "", new_password: "", confirm_new_password: "" },
    });

    const newPassword = watch("new_password");
    const confirmPassword = watch("confirm_new_password");
    const hasLength = newPassword.length >= 8;
    const passwordsMatch = confirmPassword.length > 0 && confirmPassword === newPassword;

    const onSubmit = async (data) => {
        const [, error] = await accountService.changeMyPassword({
            current_password: data.current_password,
            new_password: data.new_password,
        });

        if (error !== null) {
            toast.error(
                "Password not updated",
                parseErrorDetail(error, "Something went wrong, please try again."),
                5000
            );
            return;
        }

        toast.success("Password updated", "Your password has been changed successfully.");
        modal.resolve();
        onClose();
    };

    return (
        <Modal name="change-password" id={modal.id} onClose={onClose}>
            <Form className="modal-form change-password-modal-form" onSubmit={handleSubmit(onSubmit)}>
                <div className="change-password-modal-header">
                    <div className="change-password-modal-icon">
                        <i className="fa-solid fa-key"></i>
                    </div>
                    <div className="change-password-modal-heading">
                        <span className="change-password-modal-title">Change your password</span>
                        <span className="change-password-modal-intro">
                            Choose a new password to keep your account secure.
                        </span>
                    </div>
                </div>

                <Form.Body className="change-password-modal-body">
                    <Field label="Current password" listLabelClassName="titles-input">
                        <TextInput
                            type="password"
                            {...register("current_password", { required: true })}
                            style={errors.current_password ? { borderColor: "var(--pink)" } : {}}
                        />
                    </Field>

                    <Field label="New password" listLabelClassName="titles-input">
                        <TextInput
                            type="password"
                            {...register("new_password", {
                                required: true,
                                minLength: 8,
                            })}
                        />
                    </Field>
                    <Field label="Confirm new password" listLabelClassName="titles-input">
                        <TextInput
                            type="password"
                            {...register("confirm_new_password", {
                                required: true,
                                validate: (value) => value === newPassword,
                            })}
                        />
                    </Field>

                    <ul className="change-password-modal-requirements">
                        <li className={hasLength ? "met" : ""}>
                            <i className={hasLength ? "fa-solid fa-circle-check" : "fa-regular fa-circle"}></i>
                            At least 8 characters
                        </li>
                        <li className={passwordsMatch ? "met" : ""}>
                            <i className={passwordsMatch ? "fa-solid fa-circle-check" : "fa-regular fa-circle"}></i>
                            Passwords match
                        </li>
                    </ul>
                </Form.Body>
                <Form.Buttons>
                    <Button className="validate" label="Cancel" onClick={onClose} style={{ margin: "10px" }} />
                    <Button className="validate" label="Update password" type="submit" disabled={!isValid} style={{ margin: "10px" }} />
                </Form.Buttons>
            </Form>
        </Modal>
    );
});

export default ChangePasswordModal;
